"use client";

import React from "react";
import { Snowflake, RefreshCw, ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  ProductSortDropdown,
  ProductsFilter,
  ProductPrice,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, any>;
};

function ProductsConsumer() {
  const { products, loading, error, refetch, currentPage, totalPages, hasNext, hasPrev, nextPage, prevPage } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} data-index={i} className="flex flex-col gap-4 p-4 border border-border rounded-2xl bg-card">
            <Skeleton className="w-full aspect-square rounded-xl" />
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-10 w-full rounded-xl" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-card rounded-2xl border border-border">
        <p className="text-destructive font-medium mb-4">{"Erreur de chargement."}</p>
        <Button onClick={refetch} variant="outline" className="gap-2">
          <RefreshCw className="size-4" />
          {"Réessayer"}
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-card rounded-2xl border border-border">
        <p className="text-foreground font-semibold text-lg mb-2">{"Aucun produit trouvé"}</p>
        <p className="text-muted-foreground text-sm max-w-md">
          {"Modifiez vos filtres pour découvrir nos sélections."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product, i) => (
          <div data-index={i} key={product.id} className="group">
            <Card className="h-full border border-border rounded-2xl bg-card hover:shadow-lg transition-shadow duration-200 overflow-hidden flex flex-col justify-between py-0">
              <CardContent className="p-4 flex flex-col h-full justify-between gap-4">
                <div>
                  <div className="relative aspect-square overflow-hidden rounded-xl bg-muted/50 mb-3 flex items-center justify-center">
                    <Badge variant="outline" className="absolute top-2.5 left-2.5 z-10 bg-accent text-accent-foreground border-accent/20 gap-1 font-semibold text-[11px] px-2 py-0.5 rounded-full">
                      <Snowflake className="size-3" />
                      {"Sélection"}
                    </Badge>
                    {product.featured_image_url ? (
                      <Link to={product.link} className="block w-full h-full">
                        <img
                          src={product.featured_image_url}
                          alt={product.title}
                          className="h-full w-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </Link>
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-muted-foreground text-xs">
                        {"Visuel indisponible"}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    {product.categories && product.categories.length > 0 && (
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                        {product.categories[0].name}
                      </span>
                    )}
                    <Link to={product.link} className="block">
                      <h3 className="text-base font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {product.title}
                      </h3>
                    </Link>
                    {product.short_description && (
                      <div
                        className="text-xs text-muted-foreground line-clamp-2 [&_p]:mb-0"
                        dangerouslySetInnerHTML={{ __html: product.short_description }}
                      />
                    )}
                  </div>
                </div>

                <div className="space-y-3 pt-2 border-t border-border/60">
                  <ProductPrice product={product} className="text-lg font-bold text-foreground" />
                  <AddToCartButton
                    product={product}
                    productId={product.id}
                    className="w-full"
                    buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11 rounded-xl shadow-xs transition-colors"
                    text="Ajouter"
                    showQuantity={false}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border">
          <p className="text-sm text-muted-foreground">
            {"Page "}
            <span className="font-semibold text-foreground">{currentPage}</span>
            {" sur "}
            <span className="font-semibold text-foreground">{totalPages}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={prevPage}
              disabled={!hasPrev}
              className="rounded-lg gap-1"
            >
              <ChevronLeft className="size-4" />
              {"Précédent"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={nextPage}
              disabled={!hasNext}
              className="rounded-lg gap-1"
            >
              {"Suivant"}
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Listings() {
  const currentQueryParams =
    typeof wvcClient !== "undefined" && typeof wvcClient.getCurrentWPQueryParams === "function"
      ? wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentQueryParams);

  const [showMobileFilter, setShowMobileFilter] = React.useState(false);

  return (
    <section data-section-id="36292" id="product-listings" className="bg-background text-foreground py-10 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="hidden lg:block w-1/4 shrink-0">
              <div className="sticky top-24 space-y-6 bg-card border border-border rounded-2xl p-5 shadow-xs">
                <div className="pb-3 border-b border-border flex items-center gap-2">
                  <SlidersHorizontal className="size-4 text-primary" />
                  <h2 className="font-bold text-base text-foreground">{"Filtrer"}</h2>
                </div>
                <ProductsFilter />
              </div>
            </aside>

            <div className="lg:hidden">
              <Button
                variant="outline"
                onClick={() => setShowMobileFilter(!showMobileFilter)}
                className="w-full flex items-center justify-between h-11 px-4 rounded-xl border-border bg-card text-foreground"
              >
                <span className="flex items-center gap-2 font-semibold">
                  <SlidersHorizontal className="size-4 text-primary" />
                  {"Filtrer"}
                </span>
                <span className="text-xs text-muted-foreground">
                  {showMobileFilter ? "Masquer" : "Afficher"}
                </span>
              </Button>
              {showMobileFilter && (
                <div className="mt-4 p-5 bg-card border border-border rounded-2xl shadow-xs">
                  <ProductsFilter />
                </div>
              )}
            </div>

            <main className="w-full lg:w-3/4 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-accent/40 border border-accent/60 rounded-2xl">
                <div className="flex items-center gap-2.5">
                  <div className="size-8 rounded-full bg-accent flex items-center justify-center shrink-0">
                    <Snowflake className="size-4 text-accent-foreground" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-accent-foreground">
                      {"Conseil Sommelier"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {"Sélection rigoureuse par votre Sommelier-Conseil à Penol"}
                    </p>
                  </div>
                </div>
                <div className="shrink-0">
                  <ProductSortDropdown label="Trier par:" />
                </div>
              </div>

              <ProductsConsumer />
            </main>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}